import { Component, Prop, Host, h, Element, State } from '@stencil/core';
import { ButtonUsage, ButtonVariant, ButtonType } from './button-interface';
import { hasSlot, getClassNames, Attributes, inheritAriaAttributes, observeAriaAttributes, assertAttributeRequirements } from '../../utils';

/**
 * @slot - The button's text.
 * @slot icon - Button icon on the left.
 *
 * @part button - The component's base wrapper.
 */
@Component({
  tag: 'o2-button',
  styleUrl: 'button.scss',
  shadow: true,
})
export class O2Button {
  /**
   * Flag to indicate whether the button has content or not.
   * It is used to determine whether to render an icon-only button style.
   */
  private hasContent: boolean;

  /**
   * Flag to indicate whether the button has an icon slot or not.
   * It is used to determine whether to render an icon slot in the button.
   */
  private hasIconSlot: boolean;

  /**
   * Instance of MutationObserver
   */
  private mutationObserver: MutationObserver;

  /**
   * Reference to the host element of the component.
   */
  @Element() private el!: HTMLO2ButtonElement;

  /**
   * Inherited attributes from the host element.
   */
  @State() inheritedAttributes: Attributes = {};

  /**
   * Specify the usage of the button.
   */
  @Prop() usage?: ButtonUsage = 'filled';

  /**
   * Specify the variant of the button.
   */
  @Prop() variant?: ButtonVariant = 'primary';

  /**
   * The type of the button.
   */
  @Prop() type?: ButtonType = 'button';

  /**
   * Specify the name of the icon.
   */
  @Prop() icon?: string | undefined;

  /**
   * If `true`, button takes 100% of the space.
   * @deprecated Use `fullWidth` instead.
   */
  @Prop({ reflect: true }) block? = false;

  /**
   * If `true`, button takes 100% of the space.
   */
  @Prop({ reflect: true }) fullWidth? = false;

  /**
   * If `true`, the user cannot interact with the button.
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * This attribute instructs browsers to download a URL instead of navigating to
   * it, so the user will be prompted to save it as a local file. If the attribute
   * has a value, it is used as the pre-filled file name in the Save prompt
   * (the user can still change the file name if they want).
   */
  @Prop() download?: string | undefined;

  /**
   * Contains a URL or a URL fragment that the hyperlink points to.
   * If this property is set, an anchor tag will be rendered.
   */
  @Prop() href?: string | undefined;

  /**
   * Specifies the relationship of the target object to the link object.
   * The value is a space-separated list of [link types](https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types).
   */
  @Prop() rel?: string | undefined;

  /**
   * Specifies where to display the linked URL.
   * Only applies when an `href` is provided.
   * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
   */
  @Prop() target?: string | undefined;

  connectedCallback(): void {
    const ariaLabel = this.el.getAttribute('aria-label');
    const ariaLabelledBy = this.el.getAttribute('aria-labelledby');
    const ariaDescribedBy = this.el.getAttribute('aria-describedby');

    assertAttributeRequirements(
      'o2-button',
      {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
      },
      {
        requiredOneOf: ['aria-label', 'aria-labelledby', 'aria-describedby'],
      },
    );

    this.hasIconSlot = hasSlot(this.el, '[slot="icon"]');

    // only compatible place with LFR and all browsers
    this.mutationObserver = observeAriaAttributes(this.el as HTMLElement, () => {
      // merge attributes
      const newAttributes = inheritAriaAttributes(this.el as HTMLElement);
      this.inheritedAttributes = { ...this.inheritedAttributes, ...newAttributes };
    });
  }

  componentWillLoad(): void {
    this.hasContent = this.el.innerHTML !== '';

    // TODO: find a way how to implement outside of component and just use inheritance
    this.inheritedAttributes = inheritAriaAttributes(this.el as HTMLElement);
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect();
  }

  render() {
    const { hasContent, hasIconSlot, usage, variant, type, icon, block, fullWidth, disabled, download, href, rel, target } = this;
    const hasIcon = !!icon;

    // If the link is provided and it should be disabled change anchor to div element.
    const TagType = href ? (this.disabled ? 'div': 'a') : 'button';

    const attrs =
      TagType === 'button'
        ? { type }
        : {
            download,
            href,
            rel,
            target,
          };

    const isDisabled = disabled ? 'true' : null;

    const classNames = getClassNames(['button', `button--${usage}`, `button--${variant}`, disabled && `button--disabled`, (block || fullWidth) && `button--full-width`]);

    return (
      <Host>
        <TagType {...attrs} class={classNames} aria-disabled={isDisabled} disabled={this.href ? null : disabled} part="button" role={href ? 'link' : 'button'} {...this.inheritedAttributes}>
          {hasIconSlot ? <slot name="icon"></slot> : hasIcon && <o2-icon name={icon} size={20}></o2-icon>}
          {hasContent && <slot></slot>}
        </TagType>
      </Host>
    );
  }
}
